import { z } from 'zod'
import { codePointText } from '~/services/api/contracts/codePointText'
import { MENU_DESCRIPTION_MAX } from '~/services/api/contracts/bounds/MENU_DESCRIPTION_MAX'
import { menuScheduleRules } from '~/services/api/contracts/menuScheduleRules'
import type { NoContext } from '~/services/api/contracts/NoContext'
import { CreateMenuRequestSchema } from '~/services/api/generated'
import { menuFieldsNormalizer } from '~/services/api/contracts/normalizers/menuFieldsNormalizer'

const schema = CreateMenuRequestSchema.def.right
  .omit({ store_id: true })
  .extend({ description: codePointText(MENU_DESCRIPTION_MAX).nullish() })
  .strict()

const hasEntries = (value: unknown): boolean => Array.isArray(value) && value.length > 0

export const MenuStore = (_context: NoContext): z.ZodType<z.output<typeof schema>> =>
  z.preprocess(menuFieldsNormalizer, schema).superRefine((value, issues) => {
    menuScheduleRules(value, issues)

    const availableFrom = value.available_from
    const availableUntil = value.available_until
    if (
      typeof availableFrom === 'string' &&
      typeof availableUntil === 'string' &&
      availableUntil <= availableFrom
    ) {
      issues.addIssue({
        code: 'custom',
        message: 'The available until field must be a date after available from',
        path: ['available_until']
      })
    }

    const hasTypeIds = hasEntries(value.type_ids)
    const hasMenuTypeIds = hasEntries(value.menu_type_ids)
    const hasTypes = hasEntries(value.types)
    if (!hasTypeIds && !hasMenuTypeIds && !hasTypes) {
      issues.addIssue({
        code: 'custom',
        message: 'The type_ids field is required when none of menu_type_ids / types are present',
        path: ['type_ids']
      })
      issues.addIssue({
        code: 'custom',
        message: 'The menu_type_ids field is required when none of type_ids / types are present',
        path: ['menu_type_ids']
      })
      issues.addIssue({
        code: 'custom',
        message: 'The types field is required when none of type_ids / menu_type_ids are present',
        path: ['types']
      })
    }
  })
